import { date } from "@planningcenter/datetime-fmt"
import { shape, string } from "prop-types"
import { Component } from "react"
import {
  ageInMonthsAtTimeOfEvent,
  meetsAttendeeTypeAgeRestriction
} from "utils/age_restriction_utils"
import { gendersMatch } from "utils/genders_match"

class TypeSelectionDropdown extends Component {
  constructor(props) {
    super(props)

    this.state = { isOpen: false }
  }

  componentDidUpdate() {
    this.assignSingleAttendeeType()
  }

  componentDidMount() {
    this.assignSingleAttendeeType()
  }

  assignSingleAttendeeType() {
    if (this.props.attendeeTypeId === "") {
      let availableAttendeeTypes = this.getAvailableAttendeeTypes()

      if (availableAttendeeTypes.length === 1) {
        this.props.onChange(availableAttendeeTypes[0].id, false)
      }
    }
  }

  soldOut(type) {
    return type.quantity !== null && type.quantity <= 0
  }

  isSelected(selectedId, currentId) {
    if (selectedId === currentId) {
      return "is-selected"
    } else {
      return ""
    }
  }

  attendeeTypeToDisplay(attendeeTypes, attendeeTypeId) {
    let attendeeType = attendeeTypes.find(t => t.id === attendeeTypeId)
    return attendeeType ? attendeeType.name : "Please select"
  }

  attendeeTypeWhiteSpace(name) {
    if (name.replace(/\s/g, "").length === name.length) {
      return null
    } else if (name.length > 55) {
      return "normal"
    } else {
      return null
    }
  }

  meetsAgeRequirements(attendee, attendeeType, attendeeAge, eventStartDate) {
    const dateToUse = attendeeType.evaluateAgeAt ? attendeeType.evaluateAgeAt : eventStartDate
    if (!attendeeType.hasAgeRestrictions || attendee === false) {
      return true
    }

    if (!attendee.hasBirthdate && !attendeeAge) {
      return true
    }

    return meetsAttendeeTypeAgeRestriction(attendeeType, attendee.birthdate, dateToUse)
  }

  meetsGradeRequirements(attendee, attendeeType, attendeeGrade) {
    if (!attendeeType.hasGradeRestrictions || attendee === false) {
      return true
    }

    let minGrade = attendeeType.minGrade
    let maxGrade = attendeeType.maxGrade
    if (minGrade === null) {
      minGrade = -1
    }
    if (maxGrade === null) {
      maxGrade = 12
    }

    if (!attendee.hasGrade) {
      if (attendeeGrade) {
        return minGrade <= attendeeGrade && attendeeGrade <= maxGrade
      } else {
        return true
      }
    }
    return minGrade <= attendee.grade && attendee.grade <= maxGrade
  }

  meetsGenderRequirements(attendee, attendeeType, attendeeGender) {
    if (!attendeeType.hasGenderRestrictions || attendee === false) {
      return true
    }

    if (!attendee.hasGender && !attendeeGender) {
      return true
    }

    return gendersMatch(attendeeType.genderRestrictionUnit, attendee.gender)
  }

  ageRestrictionText(attendeeType) {
    let evaluateAgeAtText = attendeeType.evaluateAgeAt
      ? date(attendeeType.evaluateAgeAt, { year: true })
      : "event start date"
    let ageRestrictionUnitText = attendeeType.ageRestrictionUnit === 1 ? "year" : "month"
    if (attendeeType.minAge == null && attendeeType.maxAge != null) {
      if (attendeeType.maxAge === 1) {
        return `at most ${attendeeType.maxAge} ${ageRestrictionUnitText} old by ${evaluateAgeAtText}`
      } else {
        return `at most ${attendeeType.maxAge} ${ageRestrictionUnitText}s old by ${evaluateAgeAtText}`
      }
    } else if (attendeeType.minAge != null && attendeeType.maxAge == null) {
      if (attendeeType.minAge === 1) {
        return `at least ${attendeeType.minAge} ${ageRestrictionUnitText} old by ${evaluateAgeAtText}`
      } else {
        return `at least ${attendeeType.minAge} ${ageRestrictionUnitText}s old by ${evaluateAgeAtText}`
      }
    } else if (attendeeType.minAge === attendeeType.maxAge) {
      if (attendeeType.maxAge === 1) {
        return `${attendeeType.minAge} ${ageRestrictionUnitText} old by ${evaluateAgeAtText}`
      } else {
        return `${attendeeType.minAge} ${ageRestrictionUnitText}s old by ${evaluateAgeAtText}`
      }
    } else {
      return `${attendeeType.minAge} - ${attendeeType.maxAge} ${ageRestrictionUnitText} olds by ${evaluateAgeAtText}`
    }
  }

  gradeRestrictionText(attendeeType) {
    if (attendeeType.minGrade === null && attendeeType.maxGrade != null) {
      return `all grades up to ${attendeeType.maxGradeToS} grade only`
    } else if (attendeeType.minGrade != null && attendeeType.maxGrade === null) {
      return `${attendeeType.minGradeToS} grade and up`
    } else if (attendeeType.minGrade === attendeeType.maxGrade) {
      return `${attendeeType.minGradeToS} grade only`
    } else {
      return `${attendeeType.minGradeToS} - ${attendeeType.maxGradeToS} grade only`
    }
  }

  genderRestrictionText(attendeeType) {
    return attendeeType.genderRestrictionUnit === ""
      ? null
      : `${attendeeType.genderRestrictionUnit === "M" ? "males" : "females"} only`
  }

  render() {
    const { attendeeTypes, attendeeTypeId } = this.props
    const { isOpen } = this.state

    return (
      <div css={{ zIndex: this.props.zValue }}>
        <div
          className={`dropdown ${isOpen ? "is-open" : ""}`}
          onClick={() => this.setState({ isOpen: !isOpen, zIndex: this.state.zIndex + 1 })}
        >
          <button
            className="select ta-l"
            aria-label="menu"
            id={`type_selection_${this.props.zValue}`}
            aria-haspopup="menu"
            css={{ height: 40 }}
          >
            {this.attendeeTypeToDisplay(attendeeTypes, attendeeTypeId)}
          </button>
          <div
            className="dropdown__menu w-100% p-r"
            role="menu"
            aria-labelledby={`type_selection_${this.props.zValue}`}
          >
            {this.renderAvailableAttendeeTypes()}
            {this.renderSoldOutAttendeeTypes()}
            {this.renderUnavailableAttendeeTypes()}
          </div>
        </div>
      </div>
    )
  }

  renderAvailableAttendeeTypes() {
    const { attendeeTypeId, onChange } = this.props
    const availableAttendeeTypes = this.getAvailableAttendeeTypes()

    return [].concat(
      [],
      availableAttendeeTypes.map(t => (
        <button
          key={t.id}
          value={t.id}
          className={`stripped-btn dropdown__item w-100% ta-l ${this.isSelected(
            attendeeTypeId,
            t.id
          )}`}
          css={{
            whiteSpace: this.attendeeTypeWhiteSpace(t.name)
          }}
          onClick={() => onChange(t.id)}
        >
          {t.name} {t.price_string.length > 0 ? `(${t.price_string})` : ""}
          <div className="fs-5 c-tint2">
            {(t.minAge != null || t.maxAge != null) && this.ageRestrictionText(t)}
          </div>
          <div className="fs-5 c-tint2">
            {(t.minGrade != null || t.maxGrade != null) && this.gradeRestrictionText(t)}
          </div>
          <div className="fs-5 c-tint2">
            {t.genderRestrictionUnit != null && this.genderRestrictionText(t)}
          </div>
        </button>
      ))
    )
  }

  renderSoldOutAttendeeTypes() {
    const soldOutAttendeeTypes = this.getSoldOutAttendeeTypes()

    return [].concat(
      soldOutAttendeeTypes.map(t => (
        <li className="dropdown__item disabled" key={t.id} value={t.id}>
          {this.soldOut(t) && (
            <div>
              <div className="c-tint3">{t.name}</div>
              <div className="fs-5 c-ruby">
                <em>{t.price_string.length > 0 ? "Sold out" : "Full"}</em>
              </div>
            </div>
          )}
        </li>
      ))
    )
  }

  renderUnavailableAttendeeTypes() {
    const { attendee, attendeeAge, attendeeGender, attendeeGrade, eventStartDate } = this.props
    const unavailableAttendeeTypes = this.getUnavailableAttendeeTypes()

    return [].concat(
      unavailableAttendeeTypes.map(t => (
        <div key={t.id} value={t.id}>
          {!this.meetsGenderRequirements(attendee, t, attendeeGender) && (
            <div className="c-tint3 disabled" css={{ padding: "4px 12px" }}>
              <div className="unavailable-attendee-type-label fs-4">{t.name}</div>
              {this.props.currentPersonVerified && (
                <div className="fs-5 unavailable-attendee-type-description">
                  {t.name} is for {this.genderRestrictionText(t)}. Our records show that{" "}
                  {attendee.firstName} is a {attendee.genderToS}. If this is incorrect, please
                  contact {t.eventSupportContactName}.
                </div>
              )}
              {!this.props.currentPersonVerified && (
                <div className="fs-5 unavailable-attendee-type-description">
                  {attendee.firstName} does not meet gender requirements for {t.name}.
                </div>
              )}
            </div>
          )}
          {!this.meetsAgeRequirements(attendee, t, attendeeAge, eventStartDate) && (
            <div className="c-tint3 disabled" css={{ padding: 12, paddingBottom: 4 }}>
              <div className="unavailable-attendee-type-label fs-3">{t.name}</div>
              {this.props.currentPersonVerified && (
                <div className="pb-1 fs-5 unavailable-attendee-type-description">
                  {t.name} is for {this.ageRestrictionText(t)}. Our records show that{" "}
                  {attendee.firstName} will be{" "}
                  {Math.floor(ageInMonthsAtTimeOfEvent(attendee.birthdate, t.evaluateAgeAt) / 12)}{" "}
                  by {date(t.evaluateAgeAt, { year: true })}. If this is incorrect, please contact{" "}
                  {t.eventSupportContactName}.
                </div>
              )}
              {!this.props.currentPersonVerified && (
                <div className="pb-1 fs-5 unavailable-attendee-type-description">
                  {attendee.firstName} does not meet age requirements for {t.name}.
                </div>
              )}
            </div>
          )}
          {!this.meetsGradeRequirements(attendee, t, attendeeGrade) && (
            <div className="c-tint3 disabled" css={{ padding: 12, paddingBottom: 4 }}>
              <div className="unavailable-attendee-type-label fs-3">{t.name}</div>
              {this.props.currentPersonVerified && (
                <div className="pb-1 fs-5 unavailable-attendee-type-description">
                  {t.name} is for {this.gradeRestrictionText(t)}. Our records show that{" "}
                  {attendee.firstName} is in {attendee.gradeToS}. If this is incorrect, please
                  contact {t.eventSupportContactName}.
                </div>
              )}
              {!this.props.currentPersonVerified && (
                <div className="pb-1 fs-5 unavailable-attendee-type-description">
                  {attendee.firstName} does not meet grade requirements for {t.name}.
                </div>
              )}
            </div>
          )}
        </div>
      ))
    )
  }

  getAvailableAttendeeTypes() {
    const {
      attendee,
      attendeeGender,
      attendeeGrade,
      attendeeAge,
      attendeeTypes,
      eventStartDate
    } = this.props

    return attendeeTypes.filter(
      t =>
        !this.soldOut(t) &&
        this.meetsGradeRequirements(attendee, t, attendeeGrade) &&
        this.meetsAgeRequirements(attendee, t, attendeeAge, eventStartDate) &&
        this.meetsGenderRequirements(attendee, t, attendeeGender)
    )
  }

  getSoldOutAttendeeTypes() {
    const { attendeeTypes } = this.props

    return attendeeTypes.filter(t => this.soldOut(t))
  }

  getUnavailableAttendeeTypes() {
    const {
      attendee,
      attendeeGender,
      attendeeGrade,
      attendeeAge,
      attendeeTypes,
      eventStartDate
    } = this.props

    return attendeeTypes.filter(
      t =>
        !this.meetsGradeRequirements(attendee, t, attendeeGrade) ||
        !this.meetsAgeRequirements(attendee, t, attendeeAge, eventStartDate) ||
        !this.meetsGenderRequirements(attendee, t, attendeeGender)
    )
  }
}

TypeSelectionDropdown.propTypes = {
  attendee: shape({
    birthdate: string,
    firstName: string
  })
}

export default TypeSelectionDropdown
